import { Machine, assign, send, spawn, interpret } from "xstate";
import { loadMap } from "../../api/map";
import { switchMap, withLatestFrom, filter } from "rxjs/operators";
import { from, Subject, BehaviorSubject } from "rxjs";
import { queryLayers } from "../../api/graphics";
import { equals } from "ramda";
import { getVisibleLayersIds } from "../../api/layers";
import { isNotNil } from "../../libs/functional-utils";

import {
  service as downloadService,
  DownloadEventType,
} from "../download/download.machine";
import { service as searchService } from "../search/search.machine";
import { featureStore } from "../../models/features/features";
import { findIdFor } from "../../stores/helpers";
import { getBasemapToggle } from "../../api/widgets";
import { setConfig } from "../../constants/config";

const watchViewExtent = (sendEvent, onEvent) => {
  let handle;
  let view, imagery;
  const stationary$ = new Subject();
  onEvent((event) => {
    if (event.type === "WATCH_VIEW_EXTENT") {
      view = event.payload.view;
      imagery = event.payload.imagery;
      handle = view.watch(
        ["stationary", "updating"],
        (newValue, oldValue, propertyName) => {
          if (equals(propertyName, "stationary")) {
            if (equals(newValue, true)) {
              stationary$.next(newValue);
            }
            sendEvent({
              type: "COMMIT.EXTENT_DATA",
              payload: { stationary: newValue },
            });
          }
        }
      );
    }
  });

  stationary$
    .pipe(
      switchMap(() =>
        from(
          Promise.all([
            queryLayers({ countOnly: true, imagery, view }),
            getVisibleLayersIds({ view, imagery }),
          ])
        )
      )
    )
    .subscribe(([count, ids]) => {
      featureStore.setVisibleIds(ids);
      featureStore.setExtentFeatureCount(count);
      featureStore.replayAction({ view, imagery });
      downloadService.send({
        type: DownloadEventType.ASSIGN_IDS,
        payload: { visible_ids: ids },
      });
    });

  return () => {
    stationary$.unsubscribe();
    handle && handle.remove();
  };
};

export const viewClick$ = new BehaviorSubject();

viewClick$.pipe(filter((event) => isNotNil(event))).subscribe((event) => {
  downloadService.send({
    type: DownloadEventType.LOAD_DOWNLOAD_URLS,
    payload: { event },
  });
});

const loadWebmap = async ({ container, options, config }) => {
  const { imageryLayer, webmap, view } = await loadMap({
    container,
    options,
    config,
  });

  const toggleWidget = await getBasemapToggle(view);

  const visibleIds = await getVisibleLayersIds({ view, imagery: imageryLayer });

  view.ui.add(toggleWidget, "bottom-right");

  return { imageryLayer, webmap, view, toggleWidget, visibleIds };
};

export const machine = Machine(
  {
    id: "map",
    initial: "idle",
    context: {
      config: null,
      webmap: null,
      view: null,
      imagery: null,
      toggleWidget: null,
      error: null,
      loaded: false,
      stationary: false,
      map_options: {
        center: [-116.678, 46.097],
        zoom: 6,
        container: null,
      },
    },
    on: {
      ADD_WIDGET: {
        actions: [
          (context, event) => {
            if (context.view) {
              context.view.ui.add(
                event.payload.container,
                event.payload.position
              );
            }
          },
        ],
      },
    },
    states: {
      idle: {
        entry: assign({
          viewExtentRef: () => spawn(watchViewExtent),
        }),
        on: {
          LOAD: [
            {
              target: "load_map",
              actions: assign((context, event) => ({
                map_options: {
                  ...context.map_options,
                  container: event.container,
                },
                config: event.config,
              })),
            },
          ],
        },
      },
      load_map: {
        invoke: {
          id: "loadMap",
          src: (context, event) =>
            loadWebmap({
              container: context.map_options.container,
              options: context.map_options,
              config: context.config,
            }),
          onDone: {
            target: "loaded",
            actions: [
              assign((_, event) => ({
                webmap: event.data.webmap,
                view: event.data.view,
                imagery: event.data.imageryLayer,
                toggleWidget: event.data.toggleWidget,
                loaded: true,
                stationary: true,
                visibleIds: event.data.visibleIds,
              })),
              (context, event) => {
                downloadService.send({
                  type: DownloadEventType.ASSIGN_IDS,
                  payload: { visible_ids: event.data.visibleIds },
                });
                featureStore.setVisibleIds(event.data.visibleIds);
                searchService.send({
                  type: "INIT",
                  payload: {
                    view: event.data.view,
                    config: context.config,
                  },
                });
              },
              (_, event) => {
                event.data.view.ui.move("zoom", "bottom-right");
              },
            ],
          },
          onError: {
            target: "error",
            actions: assign({ error: (_, event) => event.data.error }),
          },
        },
      },
      loaded: {
        activities: ["watchViewClick"],
        entry: [
          "sendInitializeEventToDownloadMachine",
          send(
            (_, event) => ({
              type: "WATCH_VIEW_EXTENT",
              payload: {
                view: event.data.view,
                imagery: event.data.imageryLayer,
              },
            }),
            {
              to: (context) => context.viewExtentRef,
            }
          ),
        ],
        on: {
          "COMMIT.EXTENT_DATA": {
            actions: [
              assign((_, event) => ({
                stationary: event.payload.stationary,
              })),
            ],
          },
        },
      },
      error: {},
    },
  },
  {
    actions: {
      sendInitializeEventToDownloadMachine: (context, _) => {
        downloadService.send({
          type: DownloadEventType.INIT,
          payload: {
            layers: context.webmap.layers,
            view: context.view,
            imagery: context.imagery,
          },
        });
      },
    },
    activities: {
      watchViewClick: (context) => {
        context.view.on("click", (event) => viewClick$.next(event));

        return () => click$.unsubscribe();
      },
    },
  }
);

export const service = interpret(machine)
  .start();
